import { useCallback, useMemo, useRef } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

import CircularProgress from "components/circular-progress";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import useScroll from "hooks/use-scroll.hook";
import type { StaffTask } from "services/types/staff-tasks.type";

import SortCellHeader from "./sort-cell-header.component";
import StaffTaskRow from "./staff-task-row.component";
import { cellSx } from "./utils";

type StaffTaskTableProps = {
  orderingType?: "patient" | "clinic";
  tasks?: StaffTask[];
  showPatientColumn?: boolean;
  isLoading: boolean;
  hasMoreData?: boolean;
  onScroll?: () => void;
  selectedTaskIds?: number[];
  setSelectedTaskIds?: React.Dispatch<React.SetStateAction<number[]>>;
  setIsSelectAllMode?: React.Dispatch<React.SetStateAction<boolean>>;
  isSelectAllMode?: boolean;
};

export default function StaffTaskTable({
  orderingType,
  tasks,
  isLoading,
  showPatientColumn = true,
  hasMoreData,
  onScroll,
  selectedTaskIds = [],
  setSelectedTaskIds,
  setIsSelectAllMode,
  isSelectAllMode = false
}: StaffTaskTableProps) {
  const { taskListOrdering, patientTaskOrdering, handleChangeTaskOrdering } =
    useStaffTasksV2();

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!setSelectedTaskIds) return;
      if (checked) {
        setIsSelectAllMode?.(true);
      } else {
        setSelectedTaskIds([]);
        setIsSelectAllMode?.(false);
      }
    },
    [setSelectedTaskIds, setIsSelectAllMode]
  );

  const handleSelectTask = useCallback(
    (taskId: number, checked: boolean) => {
      if (!setSelectedTaskIds) return;
      if (checked) {
        setSelectedTaskIds([...selectedTaskIds, taskId]);
      } else {
        setSelectedTaskIds(selectedTaskIds.filter((id) => id !== taskId));
      }
      setIsSelectAllMode?.(false);
    },
    [setSelectedTaskIds, setIsSelectAllMode, selectedTaskIds]
  );

  const allSelected = isSelectAllMode;

  const someSelected = useMemo(() => {
    if (isSelectAllMode) return false;
    if (!tasks || tasks.length === 0) return false;
    return (
      selectedTaskIds.length > 0 &&
      tasks.some((task) => selectedTaskIds.includes(task.id))
    );
  }, [tasks, selectedTaskIds, isSelectAllMode]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useScroll({
    containerRef,
    isLoading,
    hasMoreData,
    fetchMore: onScroll ? onScroll : () => {}
  });

  // Must match the header row below: 9 fixed columns, plus the optional checkbox and Patient.
  const columnCount =
    9 + (setSelectedTaskIds ? 1 : 0) + (showPatientColumn ? 1 : 0);

  const orderingValue = useMemo(
    () => (orderingType === "patient" ? patientTaskOrdering : taskListOrdering),
    [patientTaskOrdering, taskListOrdering, orderingType]
  );

  const handleOrderingChange = useCallback(
    (field?: string) => {
      orderingType && handleChangeTaskOrdering(orderingType, field ?? "");
    },
    [handleChangeTaskOrdering, orderingType]
  );

  return (
    <Box
      width="100%"
      display="flex"
      justifyContent="space-between"
      flexDirection="column"
      sx={{
        opacity: !tasks ? 0 : 1,
        height: !tasks ? "0px !important" : undefined //to avoid table blinking on ordering change
      }}
    >
      <Box
        component={Paper}
        width="100%"
        height="100%"
        boxSizing="border-box"
        sx={{ overflowX: "unset" }}
      >
        <TableContainer
          sx={{ height: "100%", overflow: "auto" }}
          data-testid="billing-list"
        >
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead sx={{ height: "45px" }}>
              <TableRow>
                {setSelectedTaskIds && (
                  <TableCell
                    padding="none"
                    sx={{ ...cellSx, width: "50px", pl: 0.5 }}
                  >
                    <Tooltip
                      disableInteractive
                      title={
                        allSelected ? `Deselect all tasks` : `Select all tasks`
                      }
                    >
                      <Checkbox
                        indeterminate={someSelected && !allSelected}
                        checked={allSelected}
                        onChange={(_, checked) => handleSelectAll(checked)}
                      />
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell padding="none" sx={{ ...cellSx, width: "86px" }}>
                  Priority
                </TableCell>
                <TableCell padding="none" sx={cellSx}>
                  Title and Description
                </TableCell>

                {showPatientColumn && (
                  <TableCell padding="none" sx={cellSx}>
                    Patient
                  </TableCell>
                )}

                <TableCell padding="none" sx={cellSx}>
                  Assignee
                </TableCell>

                <TableCell padding="none" sx={cellSx}>
                  Assigned Tags
                </TableCell>
                <TableCell padding="none" sx={cellSx}>
                  Status
                </TableCell>

                <TableCell padding="none" sx={cellSx}>
                  {!orderingType ? (
                    "Due Date"
                  ) : (
                    <SortCellHeader
                      label="Due Date"
                      orderingField="due_at"
                      orderingValue={orderingValue}
                      onToggleSortChange={handleOrderingChange}
                    />
                  )}
                </TableCell>

                <TableCell padding="none" sx={cellSx}>
                  {!orderingType ? (
                    "Created"
                  ) : (
                    <SortCellHeader
                      label="Created"
                      orderingField="created_at"
                      orderingValue={orderingValue}
                      onToggleSortChange={handleOrderingChange}
                    />
                  )}
                </TableCell>

                <TableCell padding="none" sx={{ ...cellSx, width: "115px" }}>
                  {!orderingType ? (
                    "Updated"
                  ) : (
                    <SortCellHeader
                      label="Updated"
                      orderingField="updated_at"
                      orderingValue={orderingValue}
                      onToggleSortChange={handleOrderingChange}
                    />
                  )}
                </TableCell>

                <TableCell
                  padding="none"
                  sx={{ ...cellSx, width: "45px" }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && tasks?.length === 0 && (
                <TableRow>
                  <TableCell
                    data-testid="row-no-data-found"
                    colSpan={columnCount}
                  >
                    No tasks found
                  </TableCell>
                </TableRow>
              )}
              {tasks?.map((task) => {
                return (
                  <StaffTaskRow
                    key={task.id}
                    task={task}
                    showPatientColumn={showPatientColumn}
                    isSelected={
                      isSelectAllMode || selectedTaskIds.includes(task.id)
                    }
                    onSelectChange={(checked) =>
                      handleSelectTask(task.id, checked)
                    }
                    showCheckbox={!!setSelectedTaskIds}
                    checkboxDisabled={isSelectAllMode}
                  />
                );
              })}
              {isLoading && (
                <TableRow
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    background: "white",
                    height: "100px"
                  }}
                >
                  <TableCell sx={{ padding: 0 }} colSpan={columnCount}>
                    <CircularProgress sx={{ width: "100%", py: 1 }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Box
            data-testid="fetch-more-tasks-trigger"
            ref={triggerRef}
            sx={{ height: "16px" }}
          />
        </TableContainer>
      </Box>
    </Box>
  );
}
